import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTitle } from '../../../context/TitleContext';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCalendarAlt, FaUser, FaClock, FaPhone, FaMapMarkerAlt, FaMoneyBillWave, FaSearch, FaFilter } from 'react-icons/fa';
import { showErrorToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const DoctorReservations = () => {
    const { doctorId } = useParams();
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {
        setTitle('حجوزات الطبيب');
    }, [setTitle]);

    useEffect(() => {
        fetchReservations();
    }, [doctorId, fromDate, toDate]);

    const fetchReservations = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();
            if (fromDate) queryParams.append('from_date', fromDate);
            if (toDate) queryParams.append('to_date', toDate);
            const url = queryParams.toString()
                ? `/doctors/doctor_reservations/${doctorId}?${queryParams.toString()}`
                : `/doctors/doctor_reservations/${doctorId}`;
            const response = await apiClient.get(url);

            if (response.data.statusCode === 200 || response.data.status === 'success') {
                // Handle the new data structure
                setReservations(response.data.result || []);
            } else {
                throw new Error(response.data.message || 'فشل في جلب الحجوزات');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب الحجوزات');
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setFromDate('');
        setToDate('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'غير محدد';
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };
    
    const formatDay = (day) => {
        const daysMap = {
            'saturday': 'السبت',
            'sunday': 'الأحد',
            'monday': 'الإثنين',
            'tuesday': 'الثلاثاء',
            'wednesday': 'الأربعاء',
            'thursday': 'الخميس',
            'friday': 'الجمعة'
        };
        return daysMap[day] || day;
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل الحجوزات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">حجوزات الطبيب</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/admin-doctors')}
                    >
                        <FaArrowLeft className="mx-2" />
                        رجوع
                    </motion.button>
                </div>

                {/* Filters */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label">من تاريخ</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">إلى تاريخ</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <button
                                    className="btn btn-secondary d-flex align-items-center gap-2 w-100"
                                    onClick={handleClearFilters}
                                >
                                    <FaFilter />
                                    مسح الفلاتر
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reservations Table */}
                <div className="card shadow-sm">
                    <div className="card-body">
                        {!Array.isArray(reservations) || reservations.length === 0 ? (
                            <div className="text-center py-5">
                                <FaCalendarAlt className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد حجوزات</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-center">المريض</th>
                                            <th className="text-center">هاتف المريض</th>
                                            <th className="text-center">العيادة</th>
                                            <th className="text-center">التاريخ</th>
                                            <th className="text-center">اليوم</th>
                                            <th className="text-center">الوقت</th>
                                            <th className="text-center">نوع الكشف</th>
                                            <th className="text-center">السعر</th>
                                            <th className="text-center">حالة الدفع</th>
                                            <th className="text-center">الحالة</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reservations.map((reservation, index) => (
                                            <motion.tr
                                                key={reservation.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                className="table-row-modern"
                                            >
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        #{index + 1}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="client-info">
                                                        <FaUser className="mx-1" />
                                                        {reservation.patient_name || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="client-info">
                                                        <FaPhone className="mx-1" />
                                                        {reservation.patient_phone || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="clinic-info">
                                                        <FaMapMarkerAlt className="mx-1" />
                                                        {reservation.clinic || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="date-info">
                                                        <FaCalendarAlt className="mx-1" />
                                                        {formatDate(reservation.date)}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="date-info">
                                                        {formatDay(reservation.day)}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="time-info">
                                                        <FaClock className="mx-1" />
                                                        {reservation.slot || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="appointment-type">
                                                        {reservation.appointment_type || 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="price-info">
                                                        {reservation.appointment_price ? `${reservation.appointment_price} ج.م` : 'غير محدد'}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${reservation.payment_status === 'paid' ? 'bg-success' : reservation.payment_status === 'failed' ? 'bg-danger' : 'bg-warning'}`} style={{ color: 'white' }}>
                                                        {reservation.payment_status === 'paid' ? 'مدفوع' : 
                                                         reservation.payment_status === 'failed' ? 'فشل' : 
                                                         reservation.payment_status === 'pending' ? 'معلق' : 'غير محدد'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${reservation.status === 'reseved' ? 'bg-success' : 'bg-secondary'}`} style={{ color: 'white' }}>
                                                        {reservation.status === 'reseved' ? 'محجوز' : 'غير محدد'}
                                                    </span>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DoctorReservations;

// CSS للتصميم
const style = document.createElement('style');
style.textContent = `
    .table-modern {
        background: white;
        border-radius: 12px;
        overflow: hidden;
        border: none;
    }
    
    .table-modern thead {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
    }
    
    .table-modern thead th {
        border: none;
        padding: 1rem;
        font-weight: 600;
        font-size: 0.8rem;
        text-transform: uppercase;
    }
    
    .table-modern tbody tr {
        border-bottom: 1px solid #f1f5f9;
        transition: all 0.3s ease;
    }
    
    .table-modern tbody tr:hover {
        background-color: #f8fafc;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    .table-modern tbody td {
        border: none;
        padding: 1rem;
        vertical-align: middle;
        text-align: center;
    }
    
    .order-number-badge {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-weight: 600;
        font-size: 0.875rem;
        display: inline-block;
        min-width: 60px;
        text-align: center;
    }
    
    .client-info, .clinic-info, .date-info, .time-info, .appointment-type, .price-info {
        font-weight: 500;
        color: #374151;
    }
    
    .price-info {
        color: #059669;
        font-weight: 600;
    }
`;

// Only append if not already added
if (!document.getElementById('doctor-reservations-style')) {
    style.id = 'doctor-reservations-style';
    document.head.appendChild(style);
}